import { classnames } from 'tailwindcss-classnames';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import { useState, useEffect } from 'react';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs';
import Container from 'components/helpers/Container';
import { MissingDataSource } from 'components/helpers/EditingHelpText';

import { TabsProps, TabProps } from 'components/helpers/Tabs/_types';
import { MobileTabsLayout } from 'components/helpers/Tabs/_MobileTabsLayout';
import { DesktopHorizontalTabsLayout } from 'components/helpers/Tabs/_DesktopTabsHorizontalLayout';
import { DesktopVerticalTabsLayout } from 'components/helpers/Tabs/_DesktopTabsVerticalLayout';

const Tabs = ({ rendering, fields, ...props }: TabsProps) => {
  const { isWideScreen } = useScreenBreakpoint(); // > 990px exact match
  const categories = (fields?.tabsItems ?? []) as TabProps[];
  const firstItemId = categories.length > 0 ? categories[0].id : null; // Get the ID of the first item
  const [visibleItem, setVisibleItem] = useState<string | null>(firstItemId); // Initialize visibleItem state with the ID of the first item
  const [isOpenMobile, setIsOpenMobile] = useState<boolean[]>(categories.map(() => false));
  const isMobileView = !isWideScreen; // <= 990px equivalent
  const isVertical = fields?.switchToVertical?.value;
  const { uid: theUid } = rendering;

  // const phKey = `nmdp-tabs-content-${params.DynamicPlaceholderId}`; - Use this when dynamic placeholder works
  const toggleCategory = (index: number) => {
    // Toggle the state for the specified category
    return setIsOpenMobile((prevIsOpen) => {
      const newState = [...prevIsOpen];
      newState[index] = !newState[index];
      return newState;
    });
  };
  const toggleItemVisibility = (id: string | null) => {
    // Check if the clicked tab is already open (note doesn't react setState handle this for us?)
    if (visibleItem === id) {
      // If it is, do nothing
      return visibleItem;
    }
    return setVisibleItem((prevItem: string | null) => (prevItem === id ? null : id));
  };

  useEffect(() => {
    const theButtonOnClickEvent = (
      e: Event,
      activateTabUid: string | null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      origOnClick?: any
    ) => {
      if (isMobileView) {
        const tabIndex = Number(activateTabUid);
        toggleCategory(tabIndex);
      } else {
        setVisibleItem(activateTabUid);
      }
      return origOnClick ? origOnClick(e) : true;
    };
    // Attaches client-side on-click handler to experience editor button
    const attachToButtonClicks = (btn: HTMLElement) => {
      const activateTabUid = btn?.dataset?.nmdpTab ?? null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any

      if (activateTabUid) {
        const origOnClick = btn.onclick;
        const theButton = btn;
        theButton.onclick = (e: Event) => theButtonOnClickEvent(e, activateTabUid, origOnClick);
      }
    };
    // Only run when in experience editor
    if (!isEditorActive()) {
      return;
    }
    // Find this component in experience editor window
    const myTabContainer = document.querySelector(`div[data-uid='${theUid}']`);
    if (myTabContainer) {
      // Find the first tabstrip child of the component (as a tab could contain other tabs)
      const myTabStrip = myTabContainer.querySelector('*[data-nmdp-tabstrip="tabstrip"]');
      if (myTabStrip) {
        // Then find all child buttons that contain a data-nmdp-tab attribute and attach click handlers to them
        const myTabButtons = myTabStrip.querySelectorAll('button[data-nmdp-tab]');
        myTabButtons.forEach((btn) => attachToButtonClicks(btn as HTMLElement));
      }
    }
    const chromeTags: Element[] = Array.from(document.getElementsByTagName('code')).filter(
      (e) => e.attributes.getNamedItem('phkey') !== undefined
    );
    chromeTags.forEach((element) => {
      const phKey = element.attributes.getNamedItem('phkey')?.value ?? '';
      if (element.attributes.getNamedItem('key')?.value === undefined) {
        element.setAttribute('key', phKey);
      }
    });
    if (typeof window !== 'undefined') {
      // Refreshing chromes to put all `Add here` badges to proper places
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window?.top as any)?.Sitecore?.PageModes?.ChromeManager?.resetChromes();
    }
  }, [theUid, isMobileView]);
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  return (
    <Container size="standard" dataComponent="authorable/general/tabs" uid={rendering.uid}>
      <div className={classnames('mt-[25px]', 'lg:min-h-[430px]')}>
        {isMobileView ? (
          <MobileTabsLayout
            {...props}
            rendering={rendering}
            fields={fields}
            toggleCategory={toggleCategory}
            isOpenMobile={isOpenMobile}
          />
        ) : (
          <>
            {isVertical && (
              <DesktopVerticalTabsLayout
                {...props}
                visibleItem={visibleItem}
                rendering={rendering}
                fields={fields}
                toggleItemVisibility={toggleItemVisibility}
              />
            )}
            {!isVertical && (
              <DesktopHorizontalTabsLayout
                {...props}
                visibleItem={visibleItem}
                rendering={rendering}
                fields={fields}
                toggleItemVisibility={toggleItemVisibility}
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default Tabs;
